<template>
  <div>
    <template v-if="!showLoader">
      <AppendableList v-if="contracts.length > 0" :list="contracts" ky="contractNumber">
        <template v-slot:default="{ item, index }">
          <div class="relative mb-10 rounded-[8px] border border-solid border-gray-light bg-white">
            <Expandable :expanded="index == 0 ? true : false">
              <template #default="{ toggle, expanded }">
                <RentalObjectTemplate :rentalObject="item.rentalObject">
                  <template #default="{ address, rentalObjectCode, typeCaption }">
                    <div class="text-label absolute right-6 -top-100">
                      <span
                        class="bg-green-light px-2 py-1 rounded-sm"
                        v-if="item.status === CONTRACT_STATUSES.VALID"
                      >
                        Gällande
                      </span>
                      <span
                        class="bg-gray-light px-2 py-1 rounded-sm"
                        v-else-if="item.status === CONTRACT_STATUSES.PRELIMINARILYTERMINATED"
                      >
                        Uppsagt preliminärt
                      </span>
                      <span
                        class="bg-gray-light px-2 py-1 rounded-sm"
                        v-else-if="item.status === CONTRACT_STATUSES.TERMINATED"
                      >
                        Uppsagt
                      </span>
                      <span
                        class="bg-primary-30 px-2 py-1 rounded-sm"
                        v-else-if="item.status === CONTRACT_STATUSES.AWAITING_SIGNATURE"
                      >
                        Väntar på underskrift
                      </span>
                      <span
                        class="bg-turquoise-10 px-2 py-1 rounded-sm"
                        v-else-if="item.status === CONTRACT_STATUSES.COMING"
                      >
                        Kommande
                      </span>
                      <span class="border border-solid border-gray-light bg-white px-2 py-1 rounded-sm" v-else>
                        {{ item.status }}
                      </span>
                    </div>

                    <div
                      class="align-center flex flex-row justify-center gap-2 border-solid border-gray-light px-4 py-4 md:flex-row md:items-center md:px-6 md:py-4" :class="expanded ?? 'border-b'"
                    >
                      <div class="flex flex-col">
                        {{ item.rentalObjectType === 'apartment' ? 'Bostad' : item.rentalObjectType === 'parking' ? 'Fordonsplats' : item.rentalObjectType === 'premise' ? 'Lokal' : item.rentalObjectType === 'premises' ? 'Lokal'  : item.rentalObjectType }}
                        <h3 class="text-t6">{{ address }}</h3>
                      </div>

                      <div class="ml-auto flex">
                        <Button variant="tertiary" @click="toggle()">
                          <span class="hidden xl:inline">
                            {{ expanded ? 'Dölj detaljer' : 'Visa detaljer' }}
                          </span>

                          <template #icon-after>
                            <!-- Mobile icon -->
                            <span class="xl:hidden">
                              <component :is="expanded ? 'IconMinus' : 'IconAdd'" />
                            </span>

                            <!-- Desktop icon -->
                            <span class="hidden xl:inline">
                              <IconChevronDown :class="{ 'rotate-180': expanded }" />
                            </span>
                          </template>
                        </Button>
                      </div>
                    </div>
                  </template>
                </RentalObjectTemplate>
              </template>

              <template #body>
                <ContractListItem :contract="item" :imageUrl="item.imageUrl"/>
              </template>
            </Expandable>
          </div>
        </template>
      </AppendableList>

      <div v-else>
      </div>
    </template>
    <div v-if="showLoader" class="flex flex-col gap-4">
      <CardLoaderSmall class="mb-7 flex-1 flex-col p-4" />
    </div>
  </div>
</template>
<script>
import Loader from '@/components/ui/loader';
import RentalObjectTemplate from '@/templates/rentalobjecttemplate';
import AppendableList from '@/components/ui/appendable-list';
import Expandable from '@/components/ui/expandable';
import ContractListItem from '@/components/profile/contractlistitem';
import { CONTRACT_STATUSES } from '@/config/constants';

import IconArrowRight from '@/components/icons/IconArrowRight';
import IconChevronDown from '@/components/icons/IconChevronDown';
import IconAdd from '@/components/icons/IconAdd';
import IconMinus from '@/components/icons/IconMinus';
import Button from '@/components/ui/button';
import CardLoaderSmall from '@/components/loader/CardLoaderSmall';
import IconBoxEmptyBlank from '@/components/icons/IconBoxEmptyBlank';

export default {
  components: {
    Loader,
    AppendableList,
    Expandable,
    ContractListItem,
    RentalObjectTemplate,
    IconArrowRight,
    IconChevronDown,
    IconBoxEmptyBlank,
    Button,
    IconAdd,
    IconMinus,
    CardLoaderSmall,
  },
  data() {
    return {
      showLoader: true,
      // testAlwaysLoading: true,
      visibleContractIds: [],
      searchInputIsEmpty: true,
      CONTRACT_STATUSES,
    };
  },
  computed: {
    showSearchInput() {
      return (
        this.$store.getters.profile &&
        this.$store.getters.profile.claims &&
        this.$store.getters.profile.claims.indexOf('person') == -1
      );
    },

    contracts() {
      if (!this.searchInputIsEmpty) {
        if (this.visibleContractIds.length !== 0) {
          return this.$store.getters.contracts.filter((c) => {
            return this.visibleContractIds.indexOf(c.contractNumber) !== -1;
          });
        } else {
          return [];
        }
      } else {
        return this.$store.getters.contracts;
      }
    },

    invoicesSearchData() {
      const addresses = {};
      for (let contract of this.$store.getters.contracts) {
        if (contract && contract.rentalObject)
          addresses[contract.contractNumber] = [
            contract.rentalObject.address1,
            contract.rentalObject.caption,
            contract.contractNumber,
            contract.dwellingNumber,
            contract.fromDate,
            contract.tenants.map((t) => t.caption).join(' '), // Plockar ut beskrinvingen på varje tenant
          ].join(' ');
      }
      return addresses;
    },
  },

  methods: {
    checkInput($event) {
      this.searchInputIsEmpty = $event.target.value.length == 0;
    },

    updateList() {
      this.showLoader = true;
      this.$store.cache.delete('fetchContracts');

      return this.$store.cache.dispatch('fetchContracts').finally(() => {
        this.showLoader = false;
      });
    },
  },

  created() {
    // Load contracts and profile data
    if (!this.testAlwaysLoading) {
      new Promise((resolve) => setTimeout(resolve, 500)).then(() => {
        return Promise.all([
          this.$store.cache.dispatch('fetchProfile'),
          this.$store.cache.dispatch('fetchContracts'),
        ]).finally(() => {
          this.showLoader = false;
        });
      });
    }
  },
};
</script>
